import { BaseRowType, ColumnType, Row } from "@asayinc/component-library";

import { VOTE_STATUS_LABEL } from "src/constants/labels";
import { ListProxyCommunication } from "src/store/proxy/types";

export type ColumnDataHelpers = {
  getSort: (sortKey: string) => "asc" | "desc" | undefined;
  onOrderingChange: (data: unknown, sortKey: string) => void;
};

export type ColumnData = {
  broker: ColumnType;
  customerName?: ColumnType;
  customerEmail?: ColumnType;
  customerId: ColumnType;
  voted?: ColumnType;
};

export function getColumns(showVotedColumn: boolean, isPiiFreeBroker: boolean) {
  const columns = isPiiFreeBroker
    ? ["broker", "customerId"]
    : ["broker", "customerName", "customerEmail", "customerId"];

  if (showVotedColumn) {
    columns.push("voted");
  }

  return columns;
}

export function getColumnData(
  showVotedColumn: boolean,
  isPiiFreeBroker: boolean,
  { getSort, onOrderingChange }: ColumnDataHelpers
): ColumnData {
  const columnData: ColumnData = {
    broker: {
      sort: getSort("broker__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "broker__name"),
      style: "text",
      title: "Broker",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
      width: "120px",
    },
    customerId: {
      style: "text",
      title: "Customer ID",
    },
  };
  if (!isPiiFreeBroker) {
    columnData.customerName = {
      style: "text",
      title: "Customer Name",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
    };
    columnData.customerEmail = {
      sort: getSort("customer__email"),
      sortFn: (data: unknown) => onOrderingChange(data, "customer__email"),
      style: "text",
      title: "Email",
    };
  }

  if (showVotedColumn) {
    columnData.voted = {
      sort: getSort("vote_status"),
      sortFn: (data: unknown) => onOrderingChange(data, "vote_status"),
      style: "text",
      title: "Voted",
      width: "120px",
    };
  }

  return columnData;
}

export function getRowData(
  showVotedColumn: boolean,
  isPiiFreeBroker: boolean,
  communications: ListProxyCommunication[],
  onRowClick: (communication: ListProxyCommunication) => void,
  selectedCommunication: ListProxyCommunication | null | undefined
): Row[] {
  return communications.map((communication, idx) => {
    const handleRowClick = (data: unknown) => {
      const { communication } = data as {
        communication: ListProxyCommunication;
        idx: string;
      };
      onRowClick(communication);
    };

    const {
      id,
      broker: { logo },
      customer: { email, customerIdentifier, firstName, lastName },
      voteStatus,
    } = communication;

    const rowData: {
      [key: string]: BaseRowType;
    } = {
      broker: {
        style: "image",
        content: logo || "-",
        clickHandler: handleRowClick,
      },
      customerId: {
        content: customerIdentifier || "-",
        clickHandler: handleRowClick,
      },
    };

    if (!isPiiFreeBroker) {
      rowData.customerName = {
        content: !firstName && !lastName ? "-" : `${firstName} ${lastName}`,
        clickHandler: handleRowClick,
      };

      rowData.customerEmail = {
        content: email || "-",
        clickHandler: handleRowClick,
      };
    }

    if (showVotedColumn) {
      rowData.voted = {
        content: VOTE_STATUS_LABEL[voteStatus],
        clickHandler: handleRowClick,
      };
    }

    return {
      id: id,
      data: { communication, idx },
      rowData,
      highlighted: selectedCommunication?.id === communication.id,
    };
  });
}
